import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { allGenres } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";

const years = Array.from({ length: 10 }, (_, index) => 1980 + index);
const PAGE_SIZE = 9;

const sortOptions = [
  { value: "year-desc", label: "上映年份（新 → 旧）" },
  { value: "year-asc", label: "上映年份（旧 → 新）" },
  { value: "title", label: "片名拼音 / 字母序" },
  { value: "popularity", label: "热门程度" },
];

export default function Category() {
  usePageMeta(
    "80年代经典电影分类 - 80s电影",
    "按类型与年份筛选 1980 至 1989 年的经典电影，快速找到动作、科幻、爱情、喜剧、剧情等类型影片的完整资料与影评。",
    "80年代电影分类,经典电影筛选,80s电影,动作片,科幻片,爱情片,怀旧电影"
  );

  const [searchParams] = useSearchParams();
  const genreParam = searchParams.get("genre");
  const yearParam = searchParams.get("year");

  const [selectedGenres, setSelectedGenres] = useState<string[]>(
    genreParam ? [genreParam] : []
  );
  const [year, setYear] = useState<string>(yearParam ?? "all");
  const [sort, setSort] = useState("year-desc");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setSelectedGenres(genreParam ? [genreParam] : []);
    setYear(yearParam ?? "all");
    setPage(1);
  }, [genreParam, yearParam]);

  const toggleGenre = (genre: string) => {
    setPage(1);
    setSelectedGenres((current) =>
      current.includes(genre) ? current.filter((item) => item !== genre) : [...current, genre]
    );
  };

  const resetFilters = () => {
    setSelectedGenres([]);
    setYear("all");
    setSort("year-desc");
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = movies.filter((movie) => {
      const genreMatch =
        selectedGenres.length === 0 || selectedGenres.some((genre) => movie.genres.includes(genre));
      const yearMatch = year === "all" || String(movie.year) === year;
      return genreMatch && yearMatch;
    });

    return result.sort((a, b) => {
      switch (sort) {
        case "year-asc":
          return a.year - b.year || b.popularity - a.popularity;
        case "title":
          return a.originalTitle.localeCompare(b.originalTitle);
        case "popularity":
          return b.popularity - a.popularity;
        default:
          return b.year - a.year || b.popularity - a.popularity;
      }
    });
  }, [selectedGenres, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hasFilters = selectedGenres.length > 0 || year !== "all" || sort !== "year-desc";

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-page px-4 py-9 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影分类" }]} />
          <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
                电影分类与筛选
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                按类型、上映年份与热门程度整理 1980—1989 年的影片清单。可多选类型，筛选结果会即时更新。
              </p>
            </div>
            <p className="font-label text-sm uppercase tracking-[0.18em] text-secondary-600">
              共 <span className="text-primary-700">{filtered.length}</span> 部影片
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-100/50 p-4 md:p-5">
          <div className="flex items-center justify-between md:hidden">
            <p className="flex items-center gap-2 text-sm font-medium text-foreground-800">
              <i className="ri-filter-3-line text-primary-700"></i>
              筛选与排序
            </p>
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700"
            >
              {filtersOpen ? "收起" : "展开"}
              <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
          </div>

          <div className={`${filtersOpen ? "block" : "hidden"} mt-4 space-y-5 md:mt-0 md:block`}>
            <div>
              <div className="flex items-center justify-between">
                <p className="font-label text-xs uppercase tracking-[0.2em] text-secondary-600">影片类型</p>
                <span className="text-[11px] text-secondary-500">可多选</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {allGenres.map((genre) => {
                  const active = selectedGenres.includes(genre);
                  return (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => toggleGenre(genre)}
                      aria-pressed={active}
                      className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                        active
                          ? "border-primary-600 bg-primary-600 text-background-50"
                          : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      {genre}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-background-200 pt-4">
              <p className="font-label text-xs uppercase tracking-[0.2em] text-secondary-600">上映年份</p>
              <div className="hide-scrollbar mt-3 flex gap-2 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() => {
                    setYear("all");
                    setPage(1);
                  }}
                  className={`whitespace-nowrap rounded-md border px-3 py-1.5 font-label text-sm transition-colors ${
                    year === "all"
                      ? "border-primary-600 bg-primary-100 text-primary-800"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  全部年份
                </button>
                {years.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => {
                      setYear(String(item));
                      setPage(1);
                    }}
                    className={`whitespace-nowrap rounded-md border px-3 py-1.5 font-label text-sm transition-colors ${
                      year === String(item)
                        ? "border-primary-600 bg-primary-100 text-primary-800"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <label htmlFor="sort-select" className="font-label text-xs uppercase tracking-[0.2em] text-secondary-600">
                  排序
                </label>
                <select
                  id="sort-select"
                  value={sort}
                  onChange={(event) => {
                    setSort(event.target.value);
                    setPage(1);
                  }}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {hasFilters && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 self-start text-xs text-primary-700 hover:text-primary-600 sm:self-auto"
                >
                  <i className="ri-refresh-line"></i>
                  清除全部筛选
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8">
          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:gap-x-5 lg:grid-cols-3">
                {visible.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination page={safePage} totalPages={totalPages} onPageChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-movie-line"
              title="暂无符合条件的电影"
              description="您可以调整筛选条件，比如减少已选类型或切换年份，再试试看。"
              action={
                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-md bg-primary-700 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-800"
                >
                  重置筛选条件
                </button>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}